import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import { toast } from 'sonner';
import api from '../../lib/api';

/**
 * BillCopyViewer - Left-sliding modal with PDF viewer for bill copies
 */
export const BillCopyViewer = ({ billId, accountId, copyInfo, onOpenChange }) => {
  // copyInfo is passed from parent: { hasCopy, filepath } or undefined
  // onOpenChange callback notifies parent when modal opens/closes for row highlighting
  const [open, setOpen] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [refetching, setRefetching] = useState(false);
  const [localCopyInfo, setLocalCopyInfo] = useState(copyInfo);

  // Update local state when parent passes new info
  useEffect(() => {
    if (copyInfo) {
      setLocalCopyInfo(copyInfo);
    }
  }, [copyInfo]);

  const hasCopy = localCopyInfo?.hasCopy;
  const filepath = localCopyInfo?.filepath;

  // Handle open state change and notify parent
  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    onOpenChange?.(isOpen ? billId : null);
  };

  // Refetch bill copy info (only used when icon is grey and user clicks)
  const handleRefetch = async () => {
    if (!billId || !accountId) return;
    
    setRefetching(true);
    try {
      const response = await api.get(`/payment-verification/v2/bills/${billId}/copy-info?account_id=${accountId}`);
      setLocalCopyInfo({
        hasCopy: response.data.has_copy,
        filepath: response.data.filepath
      });
      if (response.data.has_copy) {
        toast.success('Bill copy found');
      } else {
        toast.info('Bill copy not available');
      }
    } catch (e) {
      console.error('Failed to refetch bill copy info:', e);
      toast.error('Failed to check bill copy');
    } finally {
      setRefetching(false);
    }
  };

  // Fetch the actual PDF data when modal opens
  const BILLPORTAL_PDF_API = "https://api-prod.billportal.io/api/BillData/view-bill-base64";

  const fetchPdfData = useCallback(async () => {
    if (!billId || !accountId || !hasCopy) return;
    
    setLoadingPdf(true);
    try {
      // Strategy 1: Call BillPortal API directly from browser (bypasses server network restrictions)
      if (filepath) {
        try {
          const directResponse = await fetch(`${BILLPORTAL_PDF_API}?filePath=${encodeURIComponent(filepath)}`, {
            method: 'POST',
            body: '',
          });
          const data = await directResponse.json();
          const b64 = data.base64Data || data.base64_data || data.data;
          if (b64 && !data.error) {
            const dataUrl = `data:application/pdf;base64,${b64}`;
            setPdfData(dataUrl);
            return;
          }
          // If we get here, the API responded but no PDF data (e.g. S3 key not found)
          console.warn('BillPortal API response:', directResponse.status, data.error || 'no base64 data');
        } catch (directErr) {
          console.warn('Direct BillPortal API call failed, falling back to backend proxy:', directErr.message);
        }
      }
      
      // Strategy 2: Fall back to our backend proxy
      const response = await api.get(`/payment-verification/v2/bills/${billId}/copy-view?account_id=${accountId}`);
      if (response.data.base64_data) {
        const dataUrl = `data:application/pdf;base64,${response.data.base64_data}`;
        setPdfData(dataUrl);
      }
    } catch (e) {
      console.error('Failed to fetch PDF:', e);
      toast.error('Failed to load bill PDF');
    } finally {
      setLoadingPdf(false);
    }
  }, [billId, accountId, hasCopy, filepath]);

  const handleOpen = async () => {
    if (hasCopy) {
      handleOpenChange(true);
      if (!pdfData) {
        await fetchPdfData();
      }
    }
  };

  // Icon state: grey if no copy / loading, blue if has copy
  const iconClass = hasCopy === true 
    ? "text-blue-600 hover:text-blue-700 cursor-pointer" 
    : "text-gray-400 cursor-pointer hover:text-gray-600";

  // If copyInfo not yet loaded, show loading state
  if (localCopyInfo === undefined) {
    return (
      <div className="p-1">
        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={hasCopy ? handleOpen : handleRefetch}
              disabled={refetching}
              className={`p-1 rounded transition-colors ${iconClass}`}
              data-testid={`bill-copy-${billId}`}
            >
              {refetching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileText className="h-4 w-4" />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            {hasCopy === true && 'View Bill Copy (PDF)'}
            {hasCopy === false && 'Bill copy not available - Click to refetch'}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Left-sliding sheet for PDF viewer - 50% width (reduced by 10%), allows table scroll */}
      <Sheet open={open} onOpenChange={handleOpenChange} modal={false}>
        <SheetContent 
          side="left" 
          className="!w-[50vw] !max-w-[50vw] p-0"
          style={{ width: '50vw', maxWidth: '50vw' }}
          onInteractOutside={(e) => e.preventDefault()}
          hideOverlay={true}
        >
          <SheetHeader className="p-4 border-b">
            <SheetTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Bill Copy
            </SheetTitle>
            <SheetDescription>
              Bill ID: {billId?.slice(0, 16)}...
              {filepath && <span className="block text-xs mt-1 font-mono truncate">{filepath.split('/').pop()}</span>}
            </SheetDescription>
          </SheetHeader>
          <div className="h-[calc(100vh-100px)] w-full">
            {loadingPdf ? (
              <div className="flex flex-col items-center justify-center h-full gap-2">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                <span className="text-sm text-muted-foreground">Loading PDF...</span>
              </div>
            ) : pdfData ? (
              <iframe
                src={pdfData}
                title="Bill Copy PDF"
                className="w-full h-full border-0"
                allow="fullscreen"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-muted-foreground">No bill copy available</p>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default BillCopyViewer;
